import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { Pagination } from '../../components/Pagination';
import { PrimaryButton } from '../../components/PrimaryButton';
import imgLogoCherry from '../../../assets/logo-cherry-color.webp';
import {
  getMe,
  listProReviews,
  type ProReviewRow,
} from '../../../lib/professional-api';
import { useAuth } from '../../../lib/use-auth';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

type ProfessionalProfile = {
  business_name: string | null;
  tagline: string | null;
  bio: string | null;
  city: string | null;
  travel_radius_miles: number | null;
  rating_avg: number;
  rating_count: number;
  insurance_provider: string | null;
  cover_image_url: string | null;
  cover_image_preview_url?: string | null;
  portfolio_images: Array<{
    url: string;
    caption?: string | null;
    preview_url?: string | null;
  }> | null;
  services: Array<{
    custom_price: number | null;
    custom_duration_minutes: number | null;
    service?: {
      id: string;
      name: string;
      base_price: number;
      duration_minutes: number;
      pricing_unit: 'PER_HOUR' | 'PER_BOOKING';
      currency: string;
      category?: { id: string; name: string } | null;
    } | null;
  }>;
  user?: {
    first_name: string | null;
    last_name: string | null;
    profile_pic: string | null;
    profile_pic_preview_url?: string | null;
  } | null;
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const customerName = (c: ProReviewRow['customer']): string => {
  if (!c) return 'Anonymous';
  return (
    [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Anonymous'
  );
};

export default function ProPublicProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfessionalProfile | null>(null);
  const REVIEWS_PAGE_SIZE = 5;
  const [reviews, setReviews] = useState<ProReviewRow[]>([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  // Profile loads once; reviews are paginated independently so the
  // header doesn't reflow each time the customer flips review pages.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getMe()
      .then((me) => {
        if (cancelled) return;
        setProfile((me?.info ?? null) as ProfessionalProfile | null);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(apiErrorMessage(err, 'Could not load profile.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setReviewsLoading(true);
    listProReviews({
      limit: REVIEWS_PAGE_SIZE,
      offset: (reviewsPage - 1) * REVIEWS_PAGE_SIZE,
    })
      .then((r) => {
        if (cancelled) return;
        setReviews(r.data ?? []);
        setReviewsTotal(r.total_count ?? 0);
      })
      .catch(() => {
        // Reviews are secondary — leave any prior page in place.
      })
      .finally(() => {
        if (!cancelled) setReviewsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reviewsPage]);

  const reviewsTotalPages = Math.max(
    1,
    Math.ceil(reviewsTotal / REVIEWS_PAGE_SIZE),
  );

  const displayFirstName = useMemo(() => {
    if (profile?.business_name) return profile.business_name;
    return (
      profile?.user?.first_name ??
      user?.first_name ??
      'Your profile'
    );
  }, [profile, user]);

  // Canonical `*_url` columns point at a private S3 bucket and won't load
  // in an <img>. Prefer the short-lived presigned `*_preview_url` the
  // backend signs on read.
  const avatarSrc =
    profile?.user?.profile_pic_preview_url ??
    profile?.cover_image_preview_url ??
    null;
  const avatarInitial = (
    profile?.user?.first_name ??
    user?.first_name ??
    user?.email ??
    '?'
  )
    .slice(0, 1)
    .toUpperCase();

  const portfolio = profile?.portfolio_images ?? [];
  const proServices = profile?.services ?? [];

  return (
    <div className="min-h-screen bg-[#f2eee2]">
      <HeaderPro />
      <main className="max-w-[1140px] mx-auto px-4 py-8">
        {/* Banner */}
        <div className="relative mb-14">
          <div className="h-[190px] w-full rounded-[12px] bg-white flex items-center justify-center border border-[#ede8e9] relative overflow-visible">
            <button
              onClick={() => navigate(-1)}
              className="absolute left-6 top-6 w-[44px] h-[44px] bg-[#fcfcfc] rounded-[8px] flex items-center justify-center text-[#3a3335] hover:bg-white transition-colors shadow-sm z-20"
            >
              <span className="material-icons text-[24px]">
                navigate_before
              </span>
            </button>
            <div className="w-[180px]">
              <img
                src={imgLogoCherry}
                alt="Cherry Picked"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          <div className="absolute -bottom-28 left-1/2 -translate-x-1/2 md:left-8 md:translate-x-0 w-[144px] h-[144px] rounded-[18px] overflow-hidden border-[3px] border-[#f2eee2] bg-white shadow-md z-10 flex items-center justify-center">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={displayFirstName}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[56px]">
                {avatarInitial}
              </span>
            )}
          </div>
        </div>

        {/* Identity */}
        <div className="mt-32 md:mt-0 md:ml-8 mb-12 flex flex-col items-center md:items-start text-center md:text-left px-4 md:px-0">
          <div className="md:pl-[160px] md:-mt-[48px]">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[22px] leading-[32px] tracking-[-0.02em] mb-1">
              {loading ? 'Loading…' : displayFirstName}
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[16px] mb-4">
              {profile?.tagline ||
                (profile?.city ? `Professional in ${profile.city}` : '')}
            </p>

            <div className="flex flex-wrap justify-center md:justify-start gap-4 md:gap-6">
              {profile?.insurance_provider ? (
                <div className="flex items-center gap-1.5">
                  <span className="material-icons text-[#8e2a2f] !text-[18px]">
                    check_circle_outline
                  </span>
                  <span className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-medium">
                    Insured by {profile.insurance_provider}
                  </span>
                </div>
              ) : null}
              {profile?.travel_radius_miles ? (
                <div className="flex items-center gap-1.5">
                  <span className="material-icons text-[#8e2a2f] !text-[18px]">
                    place
                  </span>
                  <span className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-medium">
                    Up to {profile.travel_radius_miles} miles from{' '}
                    {profile.city ?? 'your area'}
                  </span>
                </div>
              ) : null}
              {profile?.rating_count && profile.rating_count > 0 ? (
                <div className="flex items-center gap-1.5">
                  <span className="material-icons text-[#e8cd96] !text-[18px]">
                    star
                  </span>
                  <span className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-medium">
                    {Number(profile.rating_avg).toFixed(2)} ({profile.rating_count}{' '}
                    reviews)
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-[104px] self-start">
            <div className="bg-white rounded-[10px] p-6 border border-[#ede8e9] shadow-sm flex flex-col items-start text-left">
              <div className="flex items-center gap-4 mb-2">
                <span className="material-icons text-[#8e2a2f] text-[36px]">
                  star
                </span>
                <span className="font-['DM_Sans',sans-serif] font-extrabold text-[#3a3335] text-[34px] leading-none tracking-[-1px]">
                  {profile?.rating_avg
                    ? Number(profile.rating_avg).toFixed(2)
                    : '—'}
                </span>
              </div>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
                {profile?.rating_count
                  ? `Avg. score based on ${profile.rating_count} reviews`
                  : 'No reviews yet'}
              </p>
            </div>

            <div className="bg-white rounded-[10px] p-6 border border-[#ede8e9] shadow-sm space-y-5">
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px]">
                About
              </h2>

              <div className="space-y-6">
                {profile?.city ? (
                  <div className="flex items-center gap-4">
                    <div className="w-[48px] h-[48px] rounded-[4px] bg-[#3a3335]/4% flex items-center justify-center">
                      <span className="material-icons text-[#908e8e]">
                        place
                      </span>
                    </div>
                    <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] leading-[21px] tracking-[-0.1px]">
                      {profile.travel_radius_miles
                        ? `Up to ${profile.travel_radius_miles} miles from `
                        : 'Based in '}
                      {profile.city}
                    </p>
                  </div>
                ) : null}

                {proServices[0]?.service?.category?.name ? (
                  <div className="flex items-center gap-4">
                    <div className="w-[48px] h-[48px] rounded-[4px] bg-[#3a3335]/4% flex items-center justify-center">
                      <span className="material-icons text-[#908e8e]">
                        handyman
                      </span>
                    </div>
                    <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] leading-[21px] tracking-[-0.1px]">
                      {proServices[0].service.category.name}
                    </p>
                  </div>
                ) : null}
              </div>

              <PrimaryButton
                className="w-full !h-[40px] !bg-[#8e2a2f] !rounded-[10px] !text-[14px] font-bold"
                onClick={() => navigate('/professional/messenger')}
              >
                Open messenger
              </PrimaryButton>
            </div>
          </div>

          {/* Main */}
          <div className="lg:col-span-8 space-y-8">
            {/* Bio */}
            <div className="bg-white rounded-[10px] p-6 border border-[#ede8e9] shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px]">
                  About {displayFirstName}
                </h2>
                <button
                  onClick={() => navigate('/professional/about-edit')}
                  className="flex items-center gap-2 text-[#8e2a2f] hover:opacity-80 transition-opacity"
                >
                  <span className="material-icons text-[18px]">edit</span>
                  <span className="font-['DM_Sans',sans-serif] font-bold text-[14px]">
                    Edit
                  </span>
                </button>
              </div>
              <div className="space-y-6">
                {profile?.bio ? (
                  profile.bio
                    .split(/\n\n+/)
                    .map((paragraph, i) => (
                      <p
                        key={i}
                        className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[16px] leading-[28px] whitespace-pre-wrap"
                      >
                        {paragraph}
                      </p>
                    ))
                ) : (
                  <p className="text-sm text-[#908e8e]">
                    No bio yet. Tap Edit to introduce yourself to customers.
                  </p>
                )}
              </div>
            </div>

            {/* Portfolio */}
            <div className="bg-white rounded-[10px] p-6 border border-[#ede8e9] shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px]">
                  Portfolio
                </h2>
                <button
                  onClick={() => navigate('/professional/portfolio')}
                  className="flex items-center gap-2 text-[#8e2a2f] hover:opacity-80 transition-opacity"
                >
                  <span className="material-icons text-[18px]">edit</span>
                  <span className="font-['DM_Sans',sans-serif] font-bold text-[14px]">
                    Edit
                  </span>
                </button>
              </div>
              {portfolio.length === 0 ? (
                <p className="text-sm text-[#908e8e]">
                  No photos yet. Upload some to attract customers.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {portfolio.map((img, i) => (
                    <div
                      key={`${img.url}-${i}`}
                      className="aspect-square rounded-[12px] overflow-hidden border border-[#eee] shadow-sm hover:shadow-md transition-shadow"
                    >
                      <img
                        src={img.preview_url ?? img.url}
                        alt={img.caption ?? `Work ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Services */}
            <div className="bg-white rounded-[10px] p-6 border border-[#ede8e9] shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px]">
                  Services
                </h2>
                <button
                  onClick={() => navigate('/professional/services-edit')}
                  className="flex items-center gap-2 text-[#8e2a2f] hover:opacity-80 transition-opacity"
                >
                  <span className="material-icons text-[18px]">edit</span>
                  <span className="font-['DM_Sans',sans-serif] font-bold text-[14px]">
                    Edit
                  </span>
                </button>
              </div>

              {proServices.length === 0 ? (
                <p className="text-sm text-[#908e8e]">
                  No services configured yet.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {proServices.map((row, i) => {
                    const svc = row.service;
                    if (!svc) return null;
                    const price = row.custom_price ?? Number(svc.base_price);
                    return (
                      <div
                        key={`${svc.id}-${i}`}
                        className="bg-white rounded-[12px] border border-[#ede8e9] overflow-hidden shadow-sm hover:shadow-md transition-shadow p-4"
                      >
                        <h3 className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] mb-2">
                          {svc.name}
                        </h3>
                        <p className="text-[12px] text-[#908e8e]">
                          £{price.toFixed(2)}
                          {svc.pricing_unit === 'PER_HOUR' ? '/hr' : ''}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Reviews */}
            <div className="bg-white rounded-[10px] p-6 border border-[#ede8e9] shadow-sm space-y-5">
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[16px]">
                Customer reviews
              </h2>

              {reviews.length === 0 ? (
                <p className="text-sm text-[#908e8e]">
                  No customer reviews yet — they'll appear here after your
                  first completed bookings.
                </p>
              ) : (
                <div className="space-y-8">
                  {reviews.map((review) => (
                    <div
                      key={review.id}
                      className="border border-[#f0f0f0] rounded-[16px] overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="p-8 pb-6">
                        <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[17px] mb-4">
                          {review.service?.name ?? 'Booking'}
                        </h3>
                        <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[15px] leading-[24px]">
                          {review.comment ?? '—'}
                        </p>
                      </div>
                      <div className="bg-[#8e2a2f] p-6 px-8 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-[40px] h-[40px] rounded-[8px] bg-white/10 border border-white/20 flex items-center justify-center">
                            <span className="font-bold text-white text-[16px]">
                              {customerName(review.customer)
                                .slice(0, 1)
                                .toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-['DM_Sans',sans-serif] font-bold text-white text-[15px]">
                              {customerName(review.customer)}
                            </p>
                            <p className="font-['DM_Sans',sans-serif] text-white/60 text-[13px]">
                              {formatDate(review.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full backdrop-blur-md">
                          <span className="material-icons text-[#e8cd96] text-[18px]">
                            star
                          </span>
                          <span className="font-['DM_Sans',sans-serif] font-bold text-white text-[14px]">
                            {Number(review.rating).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {reviewsTotalPages > 1 ? (
                <div className={`flex justify-center pt-4 transition-opacity ${reviewsLoading ? 'opacity-50' : ''}`}>
                  <Pagination
                    currentPage={reviewsPage}
                    totalPages={reviewsTotalPages}
                    onPageChange={setReviewsPage}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
